import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, List, Empty, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { getProvinces, getCitiesByProvinceId, getDistrictsByCityId, searchRegionsByName } from '../api/regions';

const RegionSelector = ({ visible, onCancel, onSelect }) => {
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // 获取省级数据
  useEffect(() => {
    if (visible) {
      fetchProvinces();
    }
  }, [visible]);

  // 当选择省份时，获取对应的城市
  useEffect(() => {
    if (selectedProvince) {
      fetchCities(selectedProvince.id);
      setSelectedCity(null);
      setSelectedDistrict(null);
      setDistricts([]);
    }
  }, [selectedProvince]);

  // 当选择城市时，获取对应的区县
  useEffect(() => {
    if (selectedCity) {
      fetchDistricts(selectedCity.id);
      setSelectedDistrict(null);
    }
  }, [selectedCity]);

  // 获取省级数据
  const fetchProvinces = async () => {
    try {
      setLoading(true);
      const response = await getProvinces();
      setProvinces(response.data);
      setLoading(false);
    } catch (error) {
      console.error('获取省份数据失败:', error);
      message.error('获取省份数据失败');
      setLoading(false);
    }
  };

  // 获取市级数据
  const fetchCities = async (provinceId) => {
    try {
      setLoading(true);
      const response = await getCitiesByProvinceId(provinceId);
      setCities(response.data);
      setLoading(false);
    } catch (error) {
      console.error('获取城市数据失败:', error);
      message.error('获取城市数据失败');
      setLoading(false);
    }
  };

  // 获取区县数据
  const fetchDistricts = async (cityId) => {
    try {
      setLoading(true);
      const response = await getDistrictsByCityId(cityId);
      setDistricts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('获取区县数据失败:', error);
      message.error('获取区县数据失败');
      setLoading(false);
    }
  };

  // 搜索地区
  const handleSearch = async () => {
    if (!searchValue.trim()) {
      if (isSearchMode) {
        handleResetSearch();
      }
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearchMode(true);
      const response = await searchRegionsByName(searchValue);
      
      // 按级别分类搜索结果
      const results = response.data.reduce((acc, region) => {
        acc[region.level] = acc[region.level] || [];
        acc[region.level].push(region);
        return acc;
      }, {});
      
      // 只更新有结果的级别，并保持原有数据结构
      if (results[1]) {
        setProvinces(results[1]);
      } else {
        setProvinces([]);
      }
      
      if (results[2]) {
        setCities(results[2]);
      } else {
        setCities([]);
      }
      
      if (results[3]) {
        setDistricts(results[3]);
      } else {
        setDistricts([]);
      }
      
      // 清除当前选择状态，因为搜索结果可能不包含之前选择的项
      setSelectedProvince(null);
      setSelectedCity(null);
      setSelectedDistrict(null);
      
    } catch (error) {
      console.error('搜索地区失败:', error);
      message.error('搜索地区失败，请稍后重试');
    } finally {
      setSearchLoading(false);
    }
  };

  // 选择省份
  const handleSelectProvince = (province) => {
    setSelectedProvince(province);
  };

  // 选择城市
  const handleSelectCity = (city) => {
    setSelectedCity(city);
  };

  // 选择区县
  const handleSelectDistrict = (district) => {
    setSelectedDistrict(district);
  };

  // 确认选择
  const handleConfirm = () => {
    if (!selectedDistrict) {
      message.warning('请选择到区/县级别');
      return;
    }

    // 构建完整的地区路径
    const region = {
      province: selectedProvince,
      city: selectedCity,
      district: selectedDistrict,
      path: `${selectedProvince.name}/${selectedCity.name}/${selectedDistrict.name}`,
      code: selectedDistrict.code
    };

    onSelect(region);
    onCancel();
  };

  // 重置搜索
  const handleResetSearch = () => {
    setSearchValue('');
    setIsSearchMode(false);
    fetchProvinces();
    setCities([]);
    setDistricts([]);
    setSelectedProvince(null);
    setSelectedCity(null);
    setSelectedDistrict(null);
  };

  return (
    <Modal
      title="地区选择"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button key="confirm" type="primary" onClick={handleConfirm}>
          确定
        </Button>
      ]}
      styles={{
        body: { maxHeight: 500, overflow: 'auto' }
      }}
    >
      <div className="region-search" style={{ marginBottom: 16 }}>
        <Input
          placeholder="输入地区名称搜索"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onPressEnter={handleSearch}
          suffix={
            <SearchOutlined
              onClick={handleSearch}
              style={{ cursor: searchLoading ? 'not-allowed' : 'pointer', opacity: searchLoading ? 0.5 : 1 }}
            />
          }
          style={{ width: '80%', marginRight: 8 }}
        />
        <Button onClick={handleResetSearch} disabled={searchLoading}>重置</Button>
      </div>

      <div className="region-selector" style={{ display: 'flex' }}>
        {/* 省份列表 */}
        <div className="region-column" style={{ flex: 1, borderRight: '1px solid #f0f0f0' }}>
          <div className="region-title" style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 'bold' }}>
            省份
          </div>
          <List
            size="small"
            loading={loading || searchLoading}
            dataSource={provinces}
            locale={{ emptyText: <Empty description={isSearchMode ? "搜索无结果" : "暂无数据"} /> }}
            renderItem={item => (
              <List.Item
                className={selectedProvince && selectedProvince.id === item.id ? 'selected' : ''}
                onClick={() => handleSelectProvince(item)}
                style={{
                  cursor: 'pointer',
                  padding: '8px 16px',
                  backgroundColor: selectedProvince && selectedProvince.id === item.id ? '#e6f7ff' : 'transparent'
                }}
              >
                {item.name}
              </List.Item>
            )}
          />
        </div>

        {/* 城市列表 */}
        <div className="region-column" style={{ flex: 1, borderRight: '1px solid #f0f0f0' }}>
          <div className="region-title" style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 'bold' }}>
            城市
          </div>
          <List
            size="small"
            loading={loading || searchLoading}
            dataSource={cities}
            locale={{ emptyText: <Empty description={isSearchMode ? "搜索无结果" : "请先选择省份"} /> }}
            renderItem={item => (
              <List.Item
                className={selectedCity && selectedCity.id === item.id ? 'selected' : ''}
                onClick={() => handleSelectCity(item)}
                style={{
                  cursor: 'pointer',
                  padding: '8px 16px',
                  backgroundColor: selectedCity && selectedCity.id === item.id ? '#e6f7ff' : 'transparent'
                }}
              >
                {item.name}
              </List.Item>
            )}
          />
        </div>

        {/* 区县列表 */}
        <div className="region-column" style={{ flex: 1 }}>
          <div className="region-title" style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 'bold' }}>
            区/县
          </div>
          <List
            size="small"
            loading={loading || searchLoading}
            dataSource={districts}
            locale={{ emptyText: <Empty description={isSearchMode ? "搜索无结果" : "请先选择城市"} /> }}
            renderItem={item => (
              <List.Item
                className={selectedDistrict && selectedDistrict.id === item.id ? 'selected' : ''}
                onClick={() => handleSelectDistrict(item)}
                style={{
                  cursor: 'pointer',
                  padding: '8px 16px',
                  backgroundColor: selectedDistrict && selectedDistrict.id === item.id ? '#e6f7ff' : 'transparent'
                }}
              >
                {item.name}
              </List.Item>
            )}
          />
        </div>
      </div>

      {selectedProvince && selectedCity && selectedDistrict && (
        <div className="selected-path" style={{ marginTop: 16, padding: 8, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
          已选择: {selectedProvince.name} / {selectedCity.name} / {selectedDistrict.name}
        </div>
      )}
    </Modal>
  );
};

export default RegionSelector; 